/**
 * FoodPulseProductsDirectory — Product hub.
 *
 * Mockup-aligned layout:
 *   Row 1  · 4 mode tiles (Products · Stores · Deals · Charities)
 *   Row 2  · Geography · Category · Sub-category · Search
 *   Row 3  · Add / Suggest a Product CTA
 *   Row 4  · Category grid (24 tiles, very visible, colour-coded)
 *   Row 5  · Two-column action strip:
 *              Left  → My Lists (Wish, Shopping, Pantry, Donation)
 *              Right → Share    (Deal, Surplus, Donation)
 *            Each card has a hover-popup explaining its purpose
 *            (short mock article — editable when real copy lands).
 *   Row 6  · Alphabetical product list (filtered)
 *
 * Data source: /api/food-items (866 items across 22 categories)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, MapPin, ChevronRight, Plus, Package, Store, Tag,
  HeartHandshake, Loader2, ArrowRight,
  Apple, Carrot, Beef, Bird, Fish, Shell, Milk, Egg, Wheat, Bean, Nut,
  Leaf, Flame, Salad, Sprout, GlassWater, Boxes, Utensils, Star, Flower,
  Wine, Cookie,
  Heart, ShoppingCart, Home as HomeIcon, HandHeart,
  BadgePercent, PackageOpen, HeartPulse,
  Layers, ChevronDown,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from "@/components/ui/hover-card";
import { PRODUCT_TAXONOMY, findNode } from "@/config/productTaxonomy";

const API = process.env.REACT_APP_BACKEND_URL;

// ── The 4 top-of-page mode tiles ─────────────────────────────────
const MODE_TILES = [
  { key: "products",   label: "Products / Food Items",
    icon: Package,    to: "/food-pulse/products",
    color: "#7C3AED", bg: "#F3EDFE" },
  { key: "vendors",    label: "Stores / Farms / Vendors / Restaurants",
    icon: Store,      to: "/food-pulse/businesses",
    color: "#EA580C", bg: "#FFF0E8" },
  { key: "deals",      label: "Deals / Offers / Surplus / Coupons",
    icon: Tag,        to: "/food-pulse/deals",
    color: "#0EA5A4", bg: "#E4F5F5" },
  { key: "charities",  label: "Charities / Donations / Volunteers",
    icon: HeartHandshake, to: "/food-pulse/community/charities",
    color: "#B91C1C", bg: "#FEE8E8" },
];

// ── 24 product-category tiles ────────────────────────────────────
// Each maps to a `category` value in the food_items collection. The
// colour/icon vocabulary makes the grid unmistakable at a glance.
const CATEGORY_TILES = [
  { key: "fruits",             label: "Fruits",             icon: Apple,      color: "#DC2626", bg: "#FEF2F2" },
  { key: "vegetables",         label: "Vegetables",         icon: Carrot,     color: "#EA580C", bg: "#FFF7ED" },
  { key: "leafy-greens",       label: "Leafy Greens",       icon: Salad,      color: "#15803D", bg: "#F0FDF4" },
  { key: "herbs",              label: "Herbs",              icon: Leaf,       color: "#059669", bg: "#ECFDF5" },
  { key: "spices",             label: "Spices",             icon: Flame,      color: "#B45309", bg: "#FFFBEB" },
  { key: "meat",               label: "Meat",               icon: Beef,       color: "#991B1B", bg: "#FEF2F2" },
  { key: "poultry",            label: "Poultry",            icon: Bird,       color: "#B45309", bg: "#FEFCE8" },
  { key: "fish",               label: "Fish",               icon: Fish,       color: "#0369A1", bg: "#EFF6FF" },
  { key: "seafood",            label: "Seafood",            icon: Fish,       color: "#0891B2", bg: "#ECFEFF" },
  { key: "shellfish",          label: "Shellfish",          icon: Shell,      color: "#0E7490", bg: "#ECFEFF" },
  { key: "dairy",              label: "Dairy",              icon: Milk,       color: "#374151", bg: "#F9FAFB" },
  { key: "eggs",               label: "Eggs",               icon: Egg,        color: "#B45309", bg: "#FFFBEB" },
  { key: "grains",             label: "Grains & Cereals",   icon: Wheat,      color: "#A16207", bg: "#FEFCE8" },
  { key: "legumes",            label: "Beans & Lentils",    icon: Bean,       color: "#854D0E", bg: "#FEFCE8" },
  { key: "nuts",               label: "Nuts & Seeds",       icon: Nut,        color: "#78350F", bg: "#FEF3C7" },
  { key: "sprouts",            label: "Sprouts",            icon: Sprout,     color: "#16A34A", bg: "#F0FDF4" },
  { key: "edible-flowers",     label: "Edible Flowers",     icon: Flower,     color: "#DB2777", bg: "#FDF2F8" },
  { key: "fermented",          label: "Fermented",          icon: Wine,       color: "#7C2D12", bg: "#FEF2F2" },
  { key: "beverages",          label: "Beverages",          icon: GlassWater, color: "#0EA5A4", bg: "#F0FDFA" },
  { key: "packaged",           label: "Packaged Foods",     icon: Boxes,      color: "#4338CA", bg: "#EEF2FF" },
  { key: "ready-to-eat",       label: "Ready to Eat",       icon: Utensils,   color: "#9333EA", bg: "#FAF5FF" },
  { key: "specialty-regional", label: "Specialty / Regional", icon: Star,     color: "#B45309", bg: "#FFFBEB" },
  { key: "snacks",             label: "Snacks",             icon: Cookie,     color: "#C2410C", bg: "#FFF7ED" },
  { key: "supplements",        label: "Supplements",        icon: HeartPulse, color: "#DB2777", bg: "#FDF2F8" },
];

const PERISHABLE_SET = new Set([
  "fruits","vegetables","meat","poultry","fish","seafood","shellfish",
  "dairy","eggs","herbs","leafy-greens","sprouts","edible-flowers",
]);

const COUNTRIES = [
  "United States","Canada","United Kingdom","India","Australia",
  "Germany","France","Mexico","Japan","Brazil","Other",
];

// ── Mock explanatory articles for the 4 personal-list cards. ─────
// Real editorial copy will replace `body` when you supply it.
// The `linkTo` route is stubbed; each list will grow into a full page
// (Wishlist, Shopping, Pantry, Donation) in the next iteration.
const MY_LIST_CARDS = [
  {
    key:   "wish",
    label: "Wish List",
    icon:  Heart,
    color: "#DB2777",
    bg:    "#FDF2F8",
    linkTo: "/food-pulse/lists/wishlist",
    lede:  "Products you want but don't have yet.",
    body:  "Add anything you'd like to try, buy on the next trip, or track prices on. We'll ping you when a deal, coupon or nearby vendor matches — so nothing on your list stays out of reach.",
    body2: "Add as many products to your wish list by searching our product database, finding it and selecting it to see all information and then add to your wish list. You may add items not found here and we will try to add the full information about it.",
  },
  {
    key:   "shopping",
    label: "Shopping List",
    icon:  ShoppingCart,
    color: "#0EA5A4",
    bg:    "#F0FDFA",
    linkTo: "/food-pulse/lists/shopping",
    lede:  "Your next grocery trip's must-buys.",
    body:  "Build one master list across every kitchen. Sort by aisle, share with family, tick items off as you shop, and roll purchased items into your Pantry automatically.",
  },
  {
    key:   "pantry",
    label: "Pantry/Stock List",
    icon:  HomeIcon,
    color: "#7C3AED",
    bg:    "#F3EDFE",
    linkTo: "/food-pulse/lists/pantry",
    lede:  "What's already at home — with expiry reminders.",
    body:  "Log what you have, where it's stored, and when it expires. FoodPulse gently reminds you before things spoil, suggests recipes for what's about to expire, and quietly cuts household food waste.",
  },
  {
    key:   "donation",
    label: "Donation List",
    icon:  HandHeart,
    color: "#B91C1C",
    bg:    "#FEE8E8",
    linkTo: "/food-pulse/lists/donation",
    lede:  "Food you're ready to give away.",
    body:  "Excess pantry items, surplus harvest, or a can drive at work — list it here. We match your donation with the nearest verified food bank, pantry, soup kitchen or family in need.",
  },
];

// ── Mock explanatory articles for the 3 share-action cards. ──────
const SHARE_CARDS = [
  {
    key:   "deal",
    label: "Share a Deal",
    icon:  BadgePercent,
    color: "#0EA5A4",
    bg:    "#E4F5F5",
    linkTo: "/food-pulse/spot",
    lede:  "Spotted a great price? Snap it, share it.",
    body:  "Walk into any store, capture the price tag, and post the deal to help your neighbours save. You earn Social Capital points every time a shopper checks in on your deal.",
  },
  {
    key:   "surplus",
    label: "Share Surplus",
    icon:  PackageOpen,
    color: "#EA580C",
    bg:    "#FFF0E8",
    linkTo: "/food-pulse/spot?kind=surplus",
    lede:  "Excess inventory before it spoils.",
    body:  "Restaurants, farmers, home gardeners — post surplus food so it finds a home instead of a bin. Buyers get short-window bargains; you dodge waste and recover cost.",
  },
  {
    key:   "donation",
    label: "Share Donation",
    icon:  HeartHandshake,
    color: "#B91C1C",
    bg:    "#FEE8E8",
    linkTo: "/food-pulse/spot?kind=donation",
    lede:  "Give food away — quickly and safely.",
    body:  "Offer a free share of food to nearby families or charities. FoodPulse handles the introductions, pickup logistics and lightweight safety confirmations so no meal is wasted.",
  },
];


export default function FoodPulseProductsDirectory() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const PAGE_SIZE = 60;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const q             = params.get("q") || "";
  const category      = params.get("category") || "";
  const subCat        = params.get("sub_category") || "";
  const country       = params.get("country") || "";
  const productGroup  = params.get("group") || "food";      // food | non-food | services
  const productType   = params.get("type") || "";           // e.g. perishable | non-perishable
  const productSubtyp = params.get("subtype") || "";        // e.g. fresh-fruits | grains-cereals

  const [counts, setCounts] = useState({ product_type_counts: {}, product_subtype_counts: {}, total: 0 });

  function updateParam(k, v) {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  }

  // Fetch taxonomy counts once — cheap aggregate
  useEffect(() => {
    axios.get(`${API}/api/food-items-taxonomy`)
      .then((r) => setCounts(r.data || { product_type_counts: {}, product_subtype_counts: {}, total: 0 }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    setVisibleCount(PAGE_SIZE);
    axios
      .get(`${API}/api/food-items`, {
        params: {
          q: q || undefined,
          category: category || undefined,
          sub_category: subCat || undefined,
          product_type: productType || undefined,
          product_subtype: productSubtyp || undefined,
          limit: 1000,   // pull the whole catalogue up-front (~1 KB per item)
        },
      })
      .then((r) => {
        const its = (r.data.items || []).sort((a, b) =>
          (a.common_name || "").localeCompare(b.common_name || ""));
        setItems(its);
        setTotal(r.data.total || its.length);
      })
      .catch(() => {
        setItems([]);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [q, category, subCat, productType, productSubtyp]);

  const loadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setVisibleCount((n) => Math.min(n + PAGE_SIZE, items.length));
      setLoadingMore(false);
    }, 120);
  };

  // Renders the products list (loading / empty / rows + Load-more). Called
  // both above the category tiles (primary discovery) and below (for users
  // who scrolled through the browse blocks).
  const renderProductList = () => (
    <>
      <h2 className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-3">
        {activeCat ? `Products · ${activeCat.label}` : "All Products"}
      </h2>
      {loading ? (
        <div className="text-sm opacity-70 flex items-center gap-2" data-testid="fp-products-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading products…
        </div>
      ) : loadError ? (
        <div className="rounded-2xl border-2 border-dashed p-10 text-center bg-white"
             style={{ borderColor: "#DC2626" }} data-testid="fp-products-load-error">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-40" style={{ color: "#DC2626" }} />
          <div className="font-display text-lg mb-1">We couldn't load products right now.</div>
          <div className="text-sm opacity-70 mb-3">Our servers had a hiccup. Please try again in a moment.</div>
          <button type="button" onClick={() => window.location.reload()}
                  className="rounded-full px-4 py-2 text-white font-semibold text-sm"
                  style={{ background: "#7C3AED" }}
                  data-testid="fp-products-retry">
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed p-10 text-center bg-white"
             style={{ borderColor: "#C79A3F" }} data-testid="fp-products-empty">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-40" style={{ color: "#7C3AED" }} />
          {(q || category || subCat || productType || productSubtyp) ? (
            <>
              <div className="font-display text-lg mb-1">No products match this filter.</div>
              <button type="button"
                      onClick={() => { setParams(new URLSearchParams(), { replace: true }); }}
                      className="text-sm underline" style={{ color: "#7C3AED" }}
                      data-testid="fp-products-clear">
                Clear filters
              </button>
            </>
          ) : (
            <>
              <div className="font-display text-lg mb-1">We couldn't load any products.</div>
              <div className="text-sm opacity-70 mb-3">Please try again in a moment.</div>
              <button type="button" onClick={() => window.location.reload()}
                      className="text-sm underline" style={{ color: "#7C3AED" }}
                      data-testid="fp-products-empty-retry">
                Retry
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-3 opacity-70">
            Showing {Math.min(visibleCount, items.length).toLocaleString()} of {total.toLocaleString()} — alphabetical
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fp-products-list">
            {items.slice(0, visibleCount).map((it) => <ItemRow key={it.slug || it.id} it={it} />)}
          </div>
          {visibleCount < items.length && (
            <div className="mt-6 text-center">
              <button type="button" onClick={loadMore} disabled={loadingMore}
                      className="rounded-full px-6 py-3 text-white font-semibold shadow-sm hover:shadow-md disabled:opacity-60 transition"
                      style={{ background: "#7C3AED" }}
                      data-testid="fp-products-load-more">
                {loadingMore ? "Loading…" : `Show ${Math.min(PAGE_SIZE, items.length - visibleCount)} more`}
              </button>
              <div className="text-[11px] opacity-60 mt-2">
                {(items.length - visibleCount).toLocaleString()} more items in this catalogue
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  const activeCat = useMemo(
    () => CATEGORY_TILES.find((c) => c.key === category) || null,
    [category],
  );

  // Resolve the currently-active taxonomy group / type / subtype
  // nodes — used for both the browse strip and the breadcrumbs.
  const groupNode   = useMemo(() => PRODUCT_TAXONOMY.find((n) => n.slug === productGroup) || PRODUCT_TAXONOMY[0], [productGroup]);
  const typeNode    = useMemo(() => productType ? findNode(productType, groupNode.children || []) : null, [productType, groupNode]);
  const subtypeNode = useMemo(() => productSubtyp && typeNode ? findNode(productSubtyp, typeNode.children || []) : null, [productSubtyp, typeNode]);

  return (
    <div className="min-h-screen" style={{ background: "#F1F7EE" }}
         data-testid="fp-products-directory">
      <SeoHead section="food-pulse"
               title="Food Products — FoodPulse"
               description="Search 800+ fruits, vegetables, meats, seafood, dairy and packaged foods. Add to your Wishlist, Shopping list, Pantry, or Donation list — or share a deal, surplus or donation with your community." />

      {/* ── Row 1: mode tiles ────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 pt-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3"
             data-testid="fp-mode-tiles">
          {MODE_TILES.map((m) => {
            const Icon = m.icon;
            const active = m.key === "products";
            return (
              <Link key={m.key} to={m.to}
                    className="rounded-xl p-3 sm:p-4 text-center transition-all hover:shadow-md"
                    style={active
                      ? { background: m.color, color: "#fff" }
                      : { background: "#fff", color: m.color, border: `1px solid ${m.bg}` }}
                    data-testid={`fp-mode-${m.key}`}>
                <Icon className="w-6 h-6 mx-auto mb-1" />
                <div className="text-[10px] sm:text-xs font-semibold leading-tight">
                  {m.label}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Row 2: Location + filters ───────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 py-5">
        <div className="text-center mb-3 inline-flex items-center gap-2 mx-auto justify-center w-full text-sm"
             style={{ color: "#7C3AED" }}>
          <MapPin className="w-4 h-4" />
          <span className="font-semibold">
            {country || "Choose your location"}
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <label className="block relative sm:col-span-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input type="search" defaultValue={q}
                   onBlur={(e) => updateParam("q", e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && updateParam("q", e.target.value)}
                   placeholder="Search Product"
                   className="w-full rounded-xl border pl-10 pr-4 py-2.5 text-sm bg-white"
                   style={{ borderColor: "#DDD5C8" }}
                   data-testid="fp-products-search" />
          </label>
          <select value={country} onChange={(e) => updateParam("country", e.target.value)}
                  className="rounded-xl border px-4 py-2.5 text-sm bg-white"
                  style={{ borderColor: "#DDD5C8" }}
                  data-testid="fp-products-country">
            <option value="">Country · United States</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={category} onChange={(e) => updateParam("category", e.target.value)}
                  className="rounded-xl border px-4 py-2.5 text-sm bg-white"
                  style={{ borderColor: "#DDD5C8" }}
                  data-testid="fp-products-category">
            <option value="">Category · All</option>
            {CATEGORY_TILES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <input type="text" value={subCat}
                 onChange={(e) => updateParam("sub_category", e.target.value)}
                 placeholder="Sub-Category"
                 className="rounded-xl border px-4 py-2.5 text-sm bg-white"
                 style={{ borderColor: "#DDD5C8" }}
                 data-testid="fp-products-subcat" />
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <Link to="/food-pulse/suggest-product"
                className="block text-center rounded-xl px-4 py-3 text-white font-semibold hover:shadow-md transition-shadow"
                style={{ background: "#0F3D2E" }}
                data-testid="fp-products-suggest-cta">
            <Plus className="w-4 h-4 inline-block mr-1 -mt-0.5" />
            Can't find it? Suggest a product
          </Link>
          <Link to="/food-pulse/label-scan"
                className="block text-center rounded-xl px-4 py-3 text-white font-semibold hover:shadow-md transition-shadow"
                style={{ background: "#7C3AED" }}
                data-testid="fp-products-add-cta">
            <Plus className="w-4 h-4 inline-block mr-1 -mt-0.5" />
            Scan a product label
          </Link>
        </div>
      </section>

      {/* ── Row 2.5: PRIMARY PRODUCT LIST — the ~887 items live here ── */}
      <section className="max-w-6xl mx-auto px-4 pb-4" data-testid="fp-products-section-primary">
        {renderProductList()}
      </section>

      {/* ── Row 3: Category tile grid (highly visible) ──────────── */}
      <section className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[10px] font-mono uppercase tracking-widest opacity-70">
            Shop by category
          </h2>
          {activeCat && (
            <button type="button" onClick={() => updateParam("category", "")}
                    className="text-xs underline opacity-70 hover:opacity-100"
                    data-testid="fp-clear-category">
              Clear
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 sm:gap-3"
             data-testid="fp-category-grid">
          {CATEGORY_TILES.map((c) => {
            const Icon = c.icon;
            const active = c.key === category;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => updateParam("category", active ? "" : c.key)}
                data-testid={`fp-cat-tile-${c.key}`}
                className="rounded-2xl p-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={active
                  ? { background: c.color, color: "#fff", border: `2px solid ${c.color}` }
                  : { background: c.bg, color: c.color, border: `1px solid ${c.color}22` }}
              >
                <Icon className="w-6 h-6 mx-auto mb-1.5" />
                <div className="text-[11px] font-semibold leading-tight">
                  {c.label}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Row 3.5: 3-tier taxonomy browser ─────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 pt-4" data-testid="fp-taxonomy-browser">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[10px] font-mono uppercase tracking-widest opacity-70 inline-flex items-center gap-1.5">
            <Layers className="w-3 h-3" /> Browse the taxonomy · {counts.total.toLocaleString()} products indexed
          </h2>
          {(productType || productSubtyp) && (
            <button type="button"
                    onClick={() => { updateParam("type", ""); updateParam("subtype", ""); }}
                    className="text-xs underline opacity-70 hover:opacity-100"
                    data-testid="fp-tax-clear">Clear</button>
          )}
        </div>

        {/* Tier 1 — Food · Non-Food · Services */}
        <div className="flex flex-wrap gap-2 mb-3">
          {PRODUCT_TAXONOMY.map((g) => {
            const active = g.slug === productGroup;
            return (
              <button
                key={g.slug}
                type="button"
                onClick={() => {
                  const p = new URLSearchParams(params);
                  p.set("group", g.slug);
                  p.delete("type"); p.delete("subtype");
                  setParams(p, { replace: true });
                }}
                data-testid={`fp-tax-group-${g.slug}`}
                className="text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full border transition-colors"
                style={active
                  ? { background: "#7C3AED", color: "#fff", borderColor: "#7C3AED" }
                  : { background: "#fff", color: "#7C3AED", borderColor: "#7C3AED44" }}
              >
                {g.label}
              </button>
            );
          })}
        </div>

        {/* Tier 2 — Product Type row (Perishable / Non-Perishable / …) */}
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="fp-tax-type-strip">
          {(groupNode?.children || []).map((t) => {
            const active = productType === t.slug;
            const n = counts.product_type_counts[t.slug] || 0;
            return (
              <button
                key={t.slug}
                type="button"
                onClick={() => { updateParam("type", active ? "" : t.slug); updateParam("subtype", ""); }}
                data-testid={`fp-tax-type-${t.slug}`}
                className="text-xs px-3 py-1 rounded-full border transition-colors"
                style={active
                  ? { background: "#166534", color: "#fff", borderColor: "#166534" }
                  : { background: "#fff", color: "#166534", borderColor: "#16653433" }}
                title={`${n} products`}
              >
                {t.label}
                {n > 0 && <span className="ml-1.5 opacity-70">· {n}</span>}
              </button>
            );
          })}
        </div>

        {/* Tier 3 — Subtype chips (only when a type is active) */}
        {typeNode && (typeNode.children?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="fp-tax-subtype-strip">
            {typeNode.children.map((st) => {
              const active = productSubtyp === st.slug;
              const n = counts.product_subtype_counts[st.slug] || 0;
              return (
                <button
                  key={st.slug}
                  type="button"
                  onClick={() => updateParam("subtype", active ? "" : st.slug)}
                  data-testid={`fp-tax-subtype-${st.slug}`}
                  className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                  style={active
                    ? { background: "#0E7490", color: "#fff", borderColor: "#0E7490" }
                    : n === 0
                    ? { background: "#F5F5F5", color: "#999", borderColor: "#EEE" }
                    : { background: "#fff", color: "#0E7490", borderColor: "#0E749033" }}
                  title={n === 0 ? "No products yet — you can be the first to add one!" : `${n} products`}
                >
                  {st.label}
                  {n > 0 ? <span className="ml-1 opacity-70">· {n}</span> : <span className="ml-1 opacity-40">· 0</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Breadcrumb — visible when in the tree */}
        {(typeNode || subtypeNode) && (
          <div className="text-xs mt-3 flex items-center gap-1 opacity-80" data-testid="fp-tax-breadcrumb">
            <span>{groupNode.label}</span>
            {typeNode && <><ChevronRight className="w-3 h-3" /><span>{typeNode.label}</span></>}
            {subtypeNode && <><ChevronRight className="w-3 h-3" /><span className="font-semibold">{subtypeNode.label}</span></>}
          </div>
        )}
      </section>

      {/* ── Row 4: My Lists  ↔  Share actions ───────────────────── */}
      <section className="max-w-6xl mx-auto px-4 py-8 grid gap-6 md:grid-cols-2">
        {/* Left — My Lists */}
        <div>
          <h2 className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-3">
            My Lists
          </h2>
          <div className="grid grid-cols-2 gap-2 sm:gap-3" data-testid="fp-my-lists">
            {MY_LIST_CARDS.map((L) => (
              <ListCard key={L.key} card={L} testidPrefix="fp-mylist" />
            ))}
          </div>
        </div>

        {/* Right — Share actions */}
        <div>
          <h2 className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-3">
            Share with the community
          </h2>
          <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="fp-share-actions">
            {SHARE_CARDS.map((S) => (
              <ListCard key={S.key} card={S} testidPrefix="fp-share" />
            ))}
          </div>
        </div>
      </section>

      {/* ── Row 5 (removed): product listing now lives in Row 2.5 above ── */}
    </div>
  );
}


// ── Hover-popup card for a My List / Share action ──────────────
function ListCard({ card, testidPrefix }) {
  const Icon = card.icon;
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <Link
          to={card.linkTo}
          data-testid={`${testidPrefix}-${card.key}`}
          className="block rounded-2xl p-3 sm:p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md"
          style={{ background: card.bg, color: card.color, border: `1px solid ${card.color}22` }}
        >
          <Icon className="w-6 h-6 mx-auto mb-1.5" strokeWidth={1.6} />
          <div className="text-[11px] sm:text-xs font-bold leading-tight">
            {card.label}
          </div>
        </Link>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-72 p-4"
        style={{ borderColor: `${card.color}44` }}
        data-testid={`${testidPrefix}-${card.key}-popup`}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Icon className="w-4 h-4" style={{ color: card.color }} />
          <div className="font-display text-sm font-semibold" style={{ color: card.color }}>
            {card.label}
          </div>
        </div>
        <div className="text-xs font-semibold mb-1.5" style={{ color: card.color }}>
          {card.lede}
        </div>
        <p className="text-xs leading-relaxed opacity-80">
          {card.body}
        </p>
        {card.body2 && (
          <p className="text-xs leading-relaxed opacity-80 mt-2" data-testid={`${testidPrefix}-${card.key}-popup-body2`}>
            {card.body2}
          </p>
        )}
        <Link to={card.linkTo}
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest"
              style={{ color: card.color }}>
          Open <ArrowRight className="w-3 h-3" />
        </Link>
      </HoverCardContent>
    </HoverCard>
  );
}


// ── One product row in the alphabetical list ───────────────────
function ItemRow({ it }) {
  const perishable = PERISHABLE_SET.has(it.category);
  const primaryImg = (it.images && it.images[0]?.url) || null;
  const catLabel = (CATEGORY_TILES.find((c) => c.key === it.category)?.label) || it.category;

  return (
    <Link to={`/food-pulse/food/${it.slug || it.id}`}
          className="flex items-center gap-3 rounded-2xl bg-white border p-3 hover:shadow-md transition-all hover:-translate-y-0.5"
          style={{ borderColor: "#E4DDD1" }}
          data-testid={`fp-product-item-${it.slug || it.id}`}>
      <div className="w-16 h-16 rounded-xl overflow-hidden flex-none"
           style={{ background: "#EFEBE3" }}>
        {primaryImg ? (
          <img src={primaryImg} alt={it.common_name}
               className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-6 h-6 opacity-30" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg leading-tight truncate">{it.common_name}</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {perishable && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "#DCEAD1", color: "#166534" }}>
              Perishable
            </span>
          )}
          {catLabel && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "#DCEAD1", color: "#166534" }}>
              {catLabel}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 opacity-40" />
    </Link>
  );
}
